/**
 * useEntitiesFromSearch - Bootstrap-compatible hook using search data
 *
 * This hook provides the SAME interface as useEntitiesFromAllItems but uses
 * the search service (SharedDataStore) as its data source instead of bootstrap.
 *
 * Migration guide:
 * ```tsx
 * // Before
 * import { useEntitiesFromAllItems } from 'api/aws/organizations_table/useEntitiesFromAllItems'
 * const { organizations, deals, products, isItemsLoading } = useEntitiesFromAllItems()
 *
 * // After
 * import { useEntitiesFromSearch } from 'api/search/useEntitiesFromSearch'
 * const { organizations, deals, products, isItemsLoading } = useEntitiesFromSearch()
 * ```
 *
 * Benefits:
 * - Shares data with grids (no duplicate fetching)
 * - Data available immediately if grids have been accessed
 * - Same interface - minimal code changes required
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getCommentsTable } from 'api/aws/comments/useCommentsTable'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { analyzeOnboarding } from 'components/NCO/analyzeOnboarding'
import { useMemo } from 'react'
import { Optimization, OrganizationTableItem, Task, Tasklist } from 'types'
import { SEARCH_DATA_KEY, useSearchData } from './useSearchData'

const getTasksTable = async () => {
  const url = `${baseUrl}/aws/tasks`
  const response = await axios.get(url, { params: { table: 'TASKS_TABLE' } })
  return response.data
}

interface TaskRows extends Task {
  relatedProduct: OrganizationTableItem
}

/**
 * Bootstrap-compatible hook that uses search data
 *
 * Returns the same interface as useEntitiesFromAllItems but fetches
 * data from the search service which uses SharedDataStore.
 */
export const useEntitiesFromSearch = () => {
  const qc = useQueryClient()

  // Primary source of truth: search data (SharedDataStore)
  const {
    organizations: rawOrganizations,
    deals,
    products: allProducts,
    parentClients: parents,
    isLoading: isItemsLoading,
    isFetching: isItemsFetching,
    isReady,
    meta,
  } = useSearchData()
  // Fetch tasks_table separately (not in ORGANIZATIONS_TABLE)
  const { data: tasks_table, isLoading: isTasksTableLoading, isFetching: isTasksTableFetching } = useQuery<any[]>({
    queryKey: ['tasks_table'],
    queryFn: getTasksTable,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 3,
  })

  // Subscribe to commentsTable cache (use useQuery to get reactive updates)
  const { data: commentsTable = [] } = useQuery({
    queryKey: ['commentsTable'],
    queryFn: getCommentsTable,
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
  })

  // Filter out service products (same as useEntitiesFromAllItems)
  const products = useMemo(() => {
    if (!allProducts || !allProducts.length) return []
    return allProducts.filter(p => !p?.serviceKey)
  }, [allProducts])
  // Custom products (same as useEntitiesFromAllItems)
  const custom_products = useMemo(() => {
    if (!allProducts || !allProducts.length) return []
    return allProducts.filter(p => p?.SK?.startsWith('PRODUCT_CUSTOM_')).filter(Boolean)
  }, [allProducts])

  // Other tables from query cache (same as useEntitiesFromAllItems)
  const checklists_table = qc.getQueryData<Tasklist[]>(['checklists'])
  const optimizations = qc.getQueryData<Optimization[]>(['optimizations'])

  // Progress maps (same as useEntitiesFromAllItems)
  const progressMaps = useMemo(() => {
    if (!Array.isArray(tasks_table)) return []
    return tasks_table?.filter(t => t?.SK?.startsWith('PROGRESS_')) ?? []
  }, [tasks_table])

  // Products by SK map (same as useEntitiesFromAllItems)
  const productsBySK = useMemo(() => {
    const map = new Map<string, OrganizationTableItem>()
    ;(products ?? []).forEach(p => map.set(p.SK, p))
    return map
  }, [products])

  // All tasks with related products (same as useEntitiesFromAllItems)
  const allTasks = useMemo(() => {
    if (!tasks_table?.length) return []
    const rows: TaskRows[] = tasks_table.map(t => {
      const relatedProduct = t?.productSK ? productsBySK.get(t.productSK) ?? null : null
      return {
        ...t,
        id: `${t?.PK}::${t?.SK}`,
        rowId: `${t?.PK}::${t?.SK}`,
        relatedProduct,
      }
    })
    return rows
  }, [tasks_table, productsBySK])

  // Analyzed organizations with onboarding status (same as useEntitiesFromAllItems)
  const analyzedOrganizations = useMemo(() => {
    if (!rawOrganizations || !deals) return []
    return analyzeOnboarding(rawOrganizations, deals) as OrganizationTableItem[]
  }, [rawOrganizations, deals])

  // Empty objects for compatibility (bootstrap has these, search doesn't yet)
  const totalsByMonth = {}
  const expiresAt = null
  const ttl = 0

  return {
    // Core data
    deals,
    organizations: analyzedOrganizations,
    parents,
    products,
    custom_products,

    // Bootstrap compatibility
    totalsByMonth,
    expiresAt,
    ttl,

    // Other cached data
    commentsTable,
    allTasks,
    tasks_table,
    progressMaps,
    checklists_table,
    optimizations,

    // Loading states
    isItemsLoading: isItemsLoading || isTasksTableLoading,
    isItemsFetching: isItemsFetching || isTasksTableFetching,
    isTasksTableLoading,

    // Search-specific extras
    isReady,
    meta,

    // Invalidation helper
    invalidate: () => qc.invalidateQueries({ queryKey: SEARCH_DATA_KEY }),
  }
}

/**
 * Utility to prefetch search data (useful for route preloading)
 */
export const prefetchSearchData = async (queryClient: any) => {
  await queryClient.prefetchQuery({
    queryKey: SEARCH_DATA_KEY,
    queryFn: async () => {
      const { fetchAllSearchData } = await import('./useSearchData')
      return fetchAllSearchData()
    },
  })
}

export default useEntitiesFromSearch
